/* eslint-disable no-console */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Button from '../Button';

import { IconCloseCircle } from '../icons/Icons';
import Label from '../Label';

export default class TextAreaStateless extends React.PureComponent {
    static propTypes = {
        /** html id required to connect the input with the label */
        id: PropTypes.string.isRequired,

        /** Label contents, usually text */
        label: PropTypes.node.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** html input type, i.e. "text" */
        type: PropTypes.oneOf(['text', 'password', 'search', 'email', 'tel']),

        /** Text to be used for the input placeholder */
        placeholder: PropTypes.string,

        /** The input's value. Use only when the input is controlled */
        value: PropTypes.string,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Makes the label and input arranged inline horizontally */
        isInline: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Contents of the hint message, usually text */
        hintText: PropTypes.node,

        /** Icon element to render before the children */
        iconElement: PropTypes.element,

        /** Ref to be passed to the input */
        innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.node]),

        /** Show clear button if true and there is a value */
        hasClearButton: PropTypes.bool,

        /** Clear button click handler */
        handleClearButtonClick: PropTypes.func,

        /** If true, the input will gain focus when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Additional className added to outer container for custom styling */
        className: PropTypes.string,

        /** Adds a unit (such as '$') that appears to the left of the input */
        unitBefore: PropTypes.string,

        /** Adds a unit (such as '%') that appears to the right of the input */
        unitAfter: PropTypes.string,

        /** If using unitBefore or unitAfter, you also need to supply a description
         * of that unit for screen readers. For example, if you use unitBefore="$",
         * you should also have srUnitDescription="dollars".
         */
        srUnitDescription: PropTypes.string,

        /** Id to be passed to the input's aria-describedby property */
        ariaDescribedby: PropTypes.string
    };

    static defaultProps = {
        isDisabled: false,
        isLabelVisible: true,
        value: undefined,
        type: "text",
        placeholder: undefined,
        isRequired: false,
        isInvalid: false,
        isInline: false,
        errorText: undefined,
        hintText: undefined,
        iconElement: undefined,
        innerRef: undefined,
        hasClearButton: false,
        handleClearButtonClick: undefined,
        className: undefined,
        unitBefore: undefined,
        unitAfter: undefined,
        srUnitDescription: undefined
    };

    constructor(props) {
        super(props);

        this.inputWrapperRef = React.createRef();
    }

    componentDidMount() {
        const { shouldFocusOnMount } = this.props;

        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            if (shouldFocusOnMount && this.inputWrapperRef) {
                this.inputWrapperRef.current.querySelector('input').focus();
            }
        }, 0);
    }

    componentWillUnmount() {
        // Safety in case of render thrashing
        clearTimeout(this.timeout);
    }

    render() {
        const {
            id,
            type,
            label,
            isDisabled,
            value,
            isLabelVisible,
            placeholder,
            isRequired,
            isInvalid,
            errorText,
            hintText,
            isInline,
            innerRef,
            iconElement,
            handleClearButtonClick,
            hasClearButton,
            className,
            shouldFocusOnMount,
            unitBefore,
            unitAfter,
            srUnitDescription,
            ariaDescribedby,
            ...rest
        } = this.props;

        if (process.env.NODE_ENV === 'development') {
            if ((!!unitBefore || !!unitAfter) && !srUnitDescription) {
                console.warn('~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~');
                console.warn('TextArea: You must provide a srUnitDescription when using unitBefore or unitAfter');
                console.warn('~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~');
            }
        }

        const hasIcon = iconElement !== undefined;
        const showClearButton = hasClearButton
            && typeof value === 'string'
            && value.length > 0;

        return (
            <div
                data-component="TextArea"
                className={cx(className, {
                    'sarsa-field-outer-container': !isInline,
                    'sarsa-field-outer-container-inline': isInline
                })}>
                <div className="sarsa-field-label-wrapper">
                    <Label
                        htmlFor={id}
                        isVisible={isLabelVisible}
                        isRequired={isRequired}>
                        {label}
                    </Label>
                </div>
                <div className="sarsa-field-inner-container">
                    <div
                        ref={this.inputWrapperRef}
                        className={cx('sarsa-field-input-wrapper sarsa-text-field-input-wrapper', {
                            'inline': isInline,
                            'no-label': !isLabelVisible
                        })}>
                        {hasIcon && (
                            <span className="sarsa-field-icon-content left">
                                {iconElement}
                            </span>
                        )}

                        {unitBefore && !iconElement && (
                            <span className="sarsa-field-icon-content sarsa-text-field-unit-before">
                                {unitBefore}
                            </span>
                        )}

                        <textarea
                            className={cx('sarsa-text-field-input', {
                                'is-invalid': isInvalid,
                                'with-icon': hasIcon,
                                'with-unit-before': !!unitBefore,
                                'with-unit-after': !!unitAfter,
                                'with-clear-button': showClearButton
                            })}
                            type={type}
                            id={id}
                            ref={innerRef}
                            disabled={isDisabled}
                            value={value}
                            placeholder={placeholder}
                            required={isRequired}
                            aria-invalid={isInvalid}
                            aria-describedby={cx({
                                [`${id}-error`]: !!errorText,
                                [`${id}-unit-description`]: !!srUnitDescription,
                                [ariaDescribedby]: !!ariaDescribedby
                            })}
                            {...rest} />

                        {unitAfter && (
                            <span className="sarsa-field-icon-content sarsa-text-field-unit-after">
                                {unitAfter}
                            </span>
                        )}

                        {srUnitDescription && (
                            <span className="rec-sr-only" id={`${id}-unit-description`}>
                                {`Field contents are in ${srUnitDescription}`}
                            </span>
                        )}

                        {showClearButton && (
                            <span className="sarsa-text-field-button-wrapper">
                                <Button
                                    appearance="subtle"
                                    size="xs"
                                    iconBeforeElement={<IconCloseCircle />}
                                    screenReaderTextAfter="Clear field contents"
                                    className="sarsa-text-field-clear-button"
                                    onClick={handleClearButtonClick} />
                            </span>

                        )}
                    </div>

                    <div
                        id={`${id}-error`}
                        className="sarsa-field-error-message"
                        aria-live="assertive">
                        {errorText && (
                            <>
                                <span className="req" aria-hidden="true">*</span>
                                {errorText}
                            </>
                        )}
                    </div>

                    <div
                        id={`${id}-hint`}
                        className="sarsa-field-hint-message"
                        aria-live="polite">
                        {!errorText && hintText && (
                            <>
                                <span className="hint" aria-hidden="true">*</span>
                                {hintText}
                            </>
                        )}
                    </div>

                </div>
            </div>
        );
    }
}
